import type { SkillDetail, SkillSummary } from '../types/api'
import { api } from './client'

/**
 * Workspace skills — reusable procedure documents agents load via the
 * `skill_list` / `skill_view` tools. Both humans and agents author them; the
 * background curator archives agent-authored skills after 30 days unread, which
 * is why `list` takes an `archived` flag and `unarchive` exists.
 */
export const skillsApi = {
  list: (wsId: string, opts?: { archived?: boolean }) =>
    api.get<SkillSummary[]>(
      `/api/workspaces/${wsId}/skills${opts?.archived ? '?archived=true' : ''}`,
    ),

  get: (wsId: string, name: string) =>
    api.get<SkillDetail>(
      `/api/workspaces/${wsId}/skills/${encodeURIComponent(name)}`,
    ),

  create: (
    wsId: string,
    body: {
      name: string
      description?: string
      content?: string
      tags?: string[]
    },
  ) => api.post<SkillSummary>(`/api/workspaces/${wsId}/skills`, body),

  update: (
    wsId: string,
    name: string,
    body: { description?: string; content?: string; tags?: string[] },
  ) =>
    api.patch<void>(
      `/api/workspaces/${wsId}/skills/${encodeURIComponent(name)}`,
      body,
    ),

  remove: (wsId: string, name: string) =>
    api.delete<void>(
      `/api/workspaces/${wsId}/skills/${encodeURIComponent(name)}`,
    ),

  unarchive: (wsId: string, name: string) =>
    api.post<void>(
      `/api/workspaces/${wsId}/skills/${encodeURIComponent(name)}/unarchive`,
      {},
    ),

  // Skill file paths contain slashes, so they travel in the body / query
  // string rather than as a path segment.
  removeFile: (wsId: string, name: string, path: string) =>
    api.delete<void>(
      `/api/workspaces/${wsId}/skills/${encodeURIComponent(name)}/files?path=${encodeURIComponent(path)}`,
    ),
}
